import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Chart of Accounts
  chartOfAccounts: [],
  accountsLoading: false,
  accountsError: null,
  
  // Journal Entries
  journalEntries: [],
  currentEntry: null,
  entriesLoading: false,
  entriesError: null,
  
  // Financial Periods
  financialPeriods: [],
  currentPeriod: null,
  periodsLoading: false,
  periodsError: null,
  
  // Financial Reports
  trialBalance: null,
  balanceSheet: null,
  incomeStatement: null,
  cashFlowStatement: null,
  generalLedger: null,
  nominalLedger: null,
  
  // Report States
  reportsLoading: {
    trialBalance: false,
    balanceSheet: false,
    incomeStatement: false,
    cashFlow: false,
    generalLedger: false,
    nominalLedger: false
  },
  reportsError: {
    trialBalance: null,
    balanceSheet: null,
    incomeStatement: null,
    cashFlow: null,
    generalLedger: null,
    nominalLedger: null
  },
  
  // Overdue & Defaulters
  overdueFees: [],
  defaultersList: [],
  paymentHistory: [],
  
  // Analytics & Dashboard
  dashboardData: {
    keyMetrics: {},
    revenueTrends: [],
    expenseBreakdown: {},
    cashFlow: {},
    alerts: []
  },
  
  // Audit & Compliance
  auditLogs: [],
  auditLoading: false,
  auditError: null,
  
  // Filters and Search
  filters: {
    dateRange: { start: null, end: null },
    accountType: 'all',
    status: 'all',
    period: 'current',
    entityType: 'all',
    action: 'all'
  },
  
  // UI State
  selectedTab: 0,
  sidebarOpen: true,
  
  // Print States
  printData: {
    trialBalance: null,
    balanceSheet: null,
    incomeStatement: null,
    cashFlow: null,
    generalLedger: null,
    nominalLedger: null
  }
};

const accountingSlice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {
    // Chart of Accounts Actions
    setChartOfAccounts: (state, action) => {
      state.chartOfAccounts = action.payload;
      state.accountsLoading = false;
      state.accountsError = null;
    },
    setAccountsLoading: (state, action) => {
      state.accountsLoading = action.payload;
    },
    setAccountsError: (state, action) => {
      state.accountsError = action.payload;
      state.accountsLoading = false;
    },
    addAccount: (state, action) => {
      state.chartOfAccounts.push(action.payload);
    },
    updateAccount: (state, action) => {
      const index = state.chartOfAccounts.findIndex(account => account._id === action.payload._id);
      if (index !== -1) {
        state.chartOfAccounts[index] = action.payload;
      }
    },
    deleteAccount: (state, action) => {
      state.chartOfAccounts = state.chartOfAccounts.filter(account => account._id !== action.payload);
    },
    
    // Journal Entries Actions
    setJournalEntries: (state, action) => {
      state.journalEntries = action.payload;
      state.entriesLoading = false;
      state.entriesError = null;
    },
    setEntriesLoading: (state, action) => {
      state.entriesLoading = action.payload;
    },
    setEntriesError: (state, action) => {
      state.entriesError = action.payload;
      state.entriesLoading = false;
    },
    setCurrentEntry: (state, action) => {
      state.currentEntry = action.payload;
    },
    addJournalEntry: (state, action) => {
      state.journalEntries.unshift(action.payload);
    },
    updateJournalEntry: (state, action) => {
      const index = state.journalEntries.findIndex(entry => entry._id === action.payload._id);
      if (index !== -1) {
        state.journalEntries[index] = action.payload;
      }
    },
    postJournalEntry: (state, action) => {
      const index = state.journalEntries.findIndex(entry => entry._id === action.payload._id);
      if (index !== -1) {
        state.journalEntries[index].status = 'Posted';
        state.journalEntries[index].approvedBy = action.payload.approvedBy;
        state.journalEntries[index].approvedAt = action.payload.approvedAt;
      }
    },
    
    // Financial Periods Actions
    setFinancialPeriods: (state, action) => {
      state.financialPeriods = action.payload;
      state.periodsLoading = false;
      state.periodsError = null;
    },
    setPeriodsLoading: (state, action) => {
      state.periodsLoading = action.payload;
    },
    setPeriodsError: (state, action) => {
      state.periodsError = action.payload;
      state.periodsLoading = false;
    },
    setCurrentPeriod: (state, action) => {
      state.currentPeriod = action.payload;
    },
    addFinancialPeriod: (state, action) => {
      state.financialPeriods.push(action.payload);
    },
    
    // Financial Reports Actions
    setTrialBalance: (state, action) => {
      state.trialBalance = action.payload;
      state.reportsLoading.trialBalance = false;
      state.reportsError.trialBalance = null;
    },
    setBalanceSheet: (state, action) => {
      state.balanceSheet = action.payload;
      state.reportsLoading.balanceSheet = false;
      state.reportsError.balanceSheet = null;
    },
    setIncomeStatement: (state, action) => {
      state.incomeStatement = action.payload;
      state.reportsLoading.incomeStatement = false;
      state.reportsError.incomeStatement = null;
    },
    setCashFlowStatement: (state, action) => {
      state.cashFlowStatement = action.payload;
      state.reportsLoading.cashFlow = false;
      state.reportsError.cashFlow = null;
    },
    setGeneralLedger: (state, action) => {
      state.generalLedger = action.payload;
      state.reportsLoading.generalLedger = false;
      state.reportsError.generalLedger = null;
    },
    setNominalLedger: (state, action) => {
      state.nominalLedger = action.payload;
      state.reportsLoading.nominalLedger = false;
      state.reportsError.nominalLedger = null;
    },
    
    // Report Loading States
    setReportLoading: (state, action) => {
      const { reportType, loading } = action.payload;
      state.reportsLoading[reportType] = loading;
    },
    setReportError: (state, action) => {
      const { reportType, error } = action.payload;
      state.reportsError[reportType] = error;
      state.reportsLoading[reportType] = false;
    },
    
    // Overdue & Defaulters Actions
    setOverdueFees: (state, action) => {
      state.overdueFees = action.payload;
    },
    setDefaultersList: (state, action) => {
      state.defaultersList = action.payload;
    },
    setPaymentHistory: (state, action) => {
      state.paymentHistory = action.payload;
    },
    
    // Dashboard Actions
    setDashboardData: (state, action) => {
      state.dashboardData = action.payload;
    },
    updateDashboardMetrics: (state, action) => {
      state.dashboardData.keyMetrics = { ...state.dashboardData.keyMetrics, ...action.payload };
    },
    
    // Audit Actions
    setAuditLogs: (state, action) => {
      state.auditLogs = action.payload;
      state.auditLoading = false;
      state.auditError = null;
    },
    setAuditLoading: (state, action) => {
      state.auditLoading = action.payload;
    },
    setAuditError: (state, action) => {
      state.auditError = action.payload;
      state.auditLoading = false;
    },
    
    // Filter Actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    // UI Actions
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    
    // Print Actions
    setPrintData: (state, action) => {
      const { reportType, data } = action.payload;
      state.printData[reportType] = data;
    },
    clearPrintData: (state, action) => {
      if (action.payload) {
        state.printData[action.payload] = null;
      } else {
        state.printData = initialState.printData;
      }
    },
    
    // Reset Actions
    resetAccounting: () => initialState,
    resetReports: (state) => {
      state.trialBalance = null;
      state.balanceSheet = null;
      state.incomeStatement = null;
      state.cashFlowStatement = null;
      state.generalLedger = null;
      state.nominalLedger = null;
      state.reportsLoading = initialState.reportsLoading;
      state.reportsError = initialState.reportsError;
    }
  }
});

export const {
  // Chart of Accounts
  setChartOfAccounts,
  setAccountsLoading,
  setAccountsError,
  addAccount,
  updateAccount,
  deleteAccount,
  
  // Journal Entries
  setJournalEntries,
  setEntriesLoading,
  setEntriesError,
  setCurrentEntry,
  addJournalEntry,
  updateJournalEntry,
  postJournalEntry,
  
  // Financial Periods
  setFinancialPeriods,
  setPeriodsLoading,
  setPeriodsError,
  setCurrentPeriod,
  addFinancialPeriod,
  
  // Financial Reports
  setTrialBalance,
  setBalanceSheet,
  setIncomeStatement,
  setCashFlowStatement,
  setGeneralLedger,
  setNominalLedger,
  setReportLoading,
  setReportError,
  
  // Overdue & Defaulters
  setOverdueFees,
  setDefaultersList,
  setPaymentHistory,
  
  // Dashboard
  setDashboardData,
  updateDashboardMetrics,
  
  // Audit
  setAuditLogs,
  setAuditLoading,
  setAuditError,
  
  // Filters
  setFilters,
  resetFilters,
  
  // UI
  setSelectedTab,
  setSidebarOpen,
  
  // Print
  setPrintData,
  clearPrintData,
  
  // Reset
  resetAccounting,
  resetReports
} = accountingSlice.actions;

export default accountingSlice.reducer;